import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const FINE_PER_DAY = 500;

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  successMsg: { color: "#4caf50", fontSize: "13px" }
};

class LibraryIssues extends Component {
  state = {
    tab: 0,
    issues: [],
    overdue: [],
    loading: false,
    openReturn: false,
    returnIssue: null,
    finePaid: false,
    saving: false,
    success: null,
    error: null
  };

  componentDidMount() { this.fetchAll(); }

  fetchAll = () => {
    this.fetchIssues();
    this.fetchOverdue();
  };

  fetchIssues = async () => {
    this.setState({ loading: true });
    try {
      const res = await api.get("/library/issues", { params: { status: "active", limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ issues: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  fetchOverdue = async () => {
    try {
      const res = await api.get("/library/issues", { params: { overdue: true, limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ overdue: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  calcFine = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const due = new Date(dueDate);
    if (today <= due) return 0;
    return Math.ceil((today - due) / 86400000) * FINE_PER_DAY;
  };

  openReturn = (issue) => {
    this.setState({ openReturn: true, returnIssue: issue, finePaid: false, error: null });
  };

  handleReturn = async () => {
    const { returnIssue, finePaid } = this.state;
    this.setState({ saving: true, error: null });
    try {
      await api.patch(`/library/issues/${returnIssue.id}/return`, {
        returnDate: new Date().toISOString().split("T")[0],
        finePaid
      });
      this.setState({ openReturn: false, success: "Book returned successfully.", saving: false });
      this.fetchAll();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to process return.", saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { tab, issues, overdue, loading, openReturn, returnIssue, finePaid, saving, success, error } = this.state;

    const fine = returnIssue ? this.calcFine(returnIssue.dueDate) : 0;

    const issueColumns = [
      { id: "studentNo", header: "Student No", accessorFn: i => i.student?.studentNo || i.studentNo || "—", meta: { variant: "mono" } },
      { id: "student", header: "Student", accessorFn: i => i.student ? `${i.student.firstName || ""} ${i.student.lastName || ""}`.trim() : "—" },
      { id: "book", header: "Book Title", accessorFn: i => i.book?.title || i.bookTitle || "—" },
      { id: "accessionNo", header: "Accession No", accessorFn: i => i.book?.accessionNo || "—", meta: { variant: "mono" } },
      { id: "issueDate", header: "Issue Date", accessorFn: i => i.issueDate || "—" },
      { id: "dueDate", header: "Due Date", accessorFn: i => i.dueDate || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const isOverdue = this.calcFine(info.row.original.dueDate) > 0;
          return <Pill tone={isOverdue ? "danger" : "success"}>{isOverdue ? "OVERDUE" : "Active"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const i = info.row.original;
          const isOverdue = this.calcFine(i.dueDate) > 0;
          return (
            <Button size="sm" color={isOverdue ? "warning" : "info"} simple onClick={() => this.openReturn(i)}>
              Return
            </Button>
          );
        }
      }
    ];

    const overdueColumns = [
      { id: "studentNo", header: "Student No", accessorFn: i => i.student?.studentNo || "—", meta: { variant: "mono" } },
      { id: "student", header: "Student", accessorFn: i => i.student ? `${i.student.firstName || ""} ${i.student.lastName || ""}`.trim() : "—" },
      { id: "book", header: "Book Title", accessorFn: i => i.book?.title || "—" },
      { id: "dueDate", header: "Due Date", accessorFn: i => i.dueDate || "—" },
      {
        id: "daysLate",
        header: "Days Late",
        accessorFn: i => Math.ceil((new Date() - new Date(i.dueDate)) / 86400000),
        cell: info => {
          const days = info.getValue();
          return `${days} day${days !== 1 ? "s" : ""}`;
        }
      },
      {
        id: "fine",
        header: "Fine (UGX)",
        accessorFn: i => this.calcFine(i.dueDate),
        cell: info => <strong style={{ color: "#c62828" }}>{formatUGX(info.getValue())}</strong>,
        meta: { align: "right" }
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="danger" simple onClick={() => this.openReturn(info.row.original)}>Return + Fine</Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Library Issues & Returns</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Active borrowings • Fine rate: UGX {FINE_PER_DAY.toLocaleString()}/day overdue
                  </p>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar
                tabs={["Issued", "Returned"]}
                activeTab={this.state.tab || 0}
                onTabChange={tab => this.setState({ tab })}
                actions={[]}
              />
              {success && <p className={classes.successMsg}>✓ {success}</p>}

              <Tabs value={tab} onChange={(_, v) => this.setState({ tab: v })} indicatorColor="primary" textColor="primary">
                <Tab label={`Active Issues (${issues.length})`} />
                <Tab label={`Overdue (${overdue.length})`} />
              </Tabs>

              {tab === 0 ? (
                <DataTable
                  columns={issueColumns}
                  data={issues}
                  isLoading={loading}
                  emptyState="No active issues."
                />
              ) : (
                <DataTable
                  columns={overdueColumns}
                  data={overdue}
                  isLoading={loading}
                  emptyState="✓ No overdue books."
                />
              )}
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={openReturn} onClose={() => this.setState({ openReturn: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Process Book Return" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            {returnIssue && (
              <div>
                <p style={{ margin: "0 0 8px" }}><strong>Book:</strong> {returnIssue.book?.title || "—"}</p>
                <p style={{ margin: "0 0 8px" }}>
                  <strong>Student:</strong>{" "}
                  {returnIssue.student
                    ? `${returnIssue.student.firstName} ${returnIssue.student.lastName} (${returnIssue.student.studentNo || ""})`
                    : "—"}
                </p>
                <p style={{ margin: "0 0 8px" }}><strong>Due Date:</strong> {returnIssue.dueDate || "—"}</p>
                <p style={{ margin: "0 0 16px" }}><strong>Return Date:</strong> {new Date().toLocaleDateString()}</p>

                {fine > 0 ? (
                  <div style={{ background: "#ffebee", borderRadius: "6px", padding: "12px", marginBottom: "12px" }}>
                    <p style={{ margin: "0 0 8px", color: "#c62828", fontWeight: "bold" }}>
                      Overdue Fine: UGX {fine.toLocaleString()}
                    </p>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={finePaid}
                        onChange={e => this.setState({ finePaid: e.target.checked })} />
                      <span style={{ fontSize: "14px" }}>Fine has been collected from student</span>
                    </label>
                  </div>
                ) : (
                  <div style={{ background: "#e8f5e9", borderRadius: "6px", padding: "12px" }}>
                    <p style={{ margin: 0, color: "#2e7d32" }}>✓ No overdue fine — returned on time.</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ openReturn: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleReturn} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Confirm Return"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

LibraryIssues.propTypes = { classes: PropTypes.object.isRequired };
export default withStyles(styles)(LibraryIssues);
